import { createFileRoute } from "@tanstack/react-router";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { PageSection } from "@/components/site/PageSection";

const canonicalUrl = "https://virginiabariatric.com/app-privacy-policy";
const effectiveDate = "September 29, 2026";

export const Route = createFileRoute("/app-privacy-policy")({
  head: () => ({
    meta: [
      { title: "App and Website Privacy Policy | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Privacy practices for the Virginia Bariatric website and Meta Interface app, including website usage measurement and data choices.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "App and Website Privacy Policy | Virginia Bariatric" },
        { property: "og:description", content: "Privacy practices for the Virginia Bariatric website and Meta Interface app, including website usage measurement and data choices." },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: "App and Website Privacy Policy | Virginia Bariatric" },
        { name: "twitter:description", content: "Privacy practices for the Virginia Bariatric website and Meta Interface app, including website usage measurement and data choices." },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [{ rel: "canonical", href: canonicalUrl }],
  }),
  component: AppPrivacyPolicyPage,
});

function Heading({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="mt-10 text-xl font-semibold text-brand-mid md:text-2xl">
      {children}
    </h2>
  );
}

function AppPrivacyPolicyPage() {
  return (
    <article>
      <PageSection maxWidth="4xl" className="py-10 md:py-14">
        <header>
          <h1 className="text-center text-3xl font-bold text-brand-mid md:text-4xl">Privacy Policy</h1>
          <p className="mt-2 text-center text-lg text-muted-foreground">
            Virginia Bariatric Meta Interface
          </p>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            Effective Date: {effectiveDate} | Last Updated: {effectiveDate}
          </p>
        </header>

        <div className="mt-8 space-y-4 text-foreground">
          <Heading id="website-measurement">Website Usage Measurement</Heading>
          <p>
            On selected service pages, we measure whether an appointment, qualifications, or insurance link
            comes into view and whether you click it. We also count appointment and contact form submissions
            and taps on our phone number. These counts help us understand which information visitors reach.
            We group them by page, link, language, and phone, tablet, or desktop screen size. We use a random
            browser-session identifier to count a link view once per session and associate later actions with
            the last link clicked during that session. The identifier is kept in session storage, which is
            cleared when your browser session ends. Measurement records do not contain your name, contact
            information, form answers, or medical details. Website measurement is stored in our Lovable Cloud
            database and reported in aggregate to our staff; Google Analytics also receives usage events.
            You can stop further session-based measurement by leaving the site or disabling site storage in
            your browser. Browser privacy controls may also limit Google Analytics. For questions or a request
            about your data, please use the contact information below.
          </p>
          <Heading id="introduction">1. Introduction</Heading>
          <p>
            Virginia Bariatric Meta Interface (&ldquo;the App,&rdquo; &ldquo;we,&rdquo;
            &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is operated by Virginia Bariatric Surgery, PC,
            located at 171 Elden St, Suite 3B, Herndon, VA 20170. This Privacy Policy explains what
            information we collect when you use our application on the Facebook platform, how we use
            it, who we share it with, and the choices available to you.
          </p>
          <p>
            By installing or using the App, you agree to the practices described in this Privacy
            Policy. If you do not agree, please do not use the App.
          </p>

          <Heading id="information-we-collect">2. Information We Collect</Heading>
          <h3 className="mt-4 font-semibold">a. Information from Facebook Login</h3>
          <p>
            When you connect to the App using Facebook Login, Facebook asks for your permission
            before sharing anything with us. With your consent, we receive the following basic
            profile information from your Facebook account:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your name</li>
            <li>Your Facebook user ID</li>
            <li>Your profile picture</li>
            <li>Your email address associated with your Facebook account, if you grant that permission</li>
          </ul>
          <p>
            We do not request or receive your Facebook password. We do not access your friends list,
            your posts, your photos, your messages, your likes, or any other content in your
            Facebook account unless we separately request that permission and you grant it.
          </p>

          <h3 className="mt-4 font-semibold">b. Information You Provide Directly</h3>
          <p>
            You may choose to give us additional information — for example, when you contact us for
            support, submit feedback, or complete a form within the App. We collect only what you
            choose to send us.
          </p>

          <h3 className="mt-4 font-semibold">c. Information Collected Automatically</h3>
          <p>
            Like most online services, our servers may automatically record basic technical
            information when you use the App, such as your IP address, browser or device type, and
            the date and time of your requests. We use this information for security,
            troubleshooting, and to keep the App running reliably.
          </p>

          <Heading id="how-we-use">3. How We Use Your Information</Heading>
          <p>We use the information described above only for the following purposes:</p>
          <ul className="list-disc space-y-1 pl-6">
            <li>To create and maintain your account and authenticate you when you log in</li>
            <li>To provide, operate, and personalize the features of the App</li>
            <li>To respond to your questions, requests, and support inquiries</li>
            <li>To monitor for and prevent fraud, abuse, and security incidents</li>
            <li>To diagnose technical problems and improve the App&rsquo;s performance and features</li>
            <li>To comply with applicable legal obligations</li>
          </ul>
          <p>
            We do not use your information for automated decision-making that produces legal or
            similarly significant effects.
          </p>

          <Heading id="how-we-share">4. How We Share Your Information</Heading>
          <p>
            We do not sell, rent, or trade your personal information to third parties. We share
            information only in these limited circumstances:
          </p>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Service providers:</strong> With vendors that perform services on our behalf —
              such as hosting, database storage, analytics, or customer support — under contracts
              that require them to protect your information and use it only for the services they
              provide to us.
            </li>
            <li>
              <strong>Legal requirements:</strong> When we believe in good faith that disclosure is
              required by law, regulation, legal process, or a valid governmental request, or is
              necessary to protect the rights, property, or safety of our users, the public, or us.
            </li>
            <li>
              <strong>Business transfers:</strong> If we are involved in a merger, acquisition,
              financing, or sale of assets, your information may be transferred as part of that
              transaction. We will notify you before your information becomes subject to a
              materially different privacy policy.
            </li>
            <li>
              <strong>With your consent:</strong> In any other case, only when you have asked us or
              agreed to the disclosure.
            </li>
          </ul>

          <Heading id="facebook-platform">5. Facebook Platform</Heading>
          <p>
            The App uses the Facebook platform and is subject to Facebook&rsquo;s Platform Terms and
            Developer Policies. Your use of Facebook itself is governed by Meta&rsquo;s own Data
            Policy, which is separate from this one and outside our control. We encourage you to
            review it at{" "}
            <a
              className="text-brand-mid underline"
              href="https://www.facebook.com/privacy/policy"
              rel="noopener noreferrer"
              target="_blank"
            >
              facebook.com/privacy/policy
            </a>
            .
          </p>
          <p>
            You can review and revoke the permissions you have granted to the App at any time
            through your Facebook settings, under Settings &amp; Privacy &gt; Settings &gt; Apps and
            Websites. Revoking permissions will stop future data sharing from Facebook to the App,
            but does not by itself delete information we have already received. To delete that
            information, see Section 7.
          </p>

          <Heading id="data-retention">6. Data Retention</Heading>
          <p>
            We retain your information for as long as your account remains active or as needed to
            provide the App to you. If you delete your account or revoke the App&rsquo;s access, we
            will delete or de-identify your personal information within 30 days, except where we are
            required to retain it to comply with legal obligations, resolve disputes, prevent fraud,
            or enforce our agreements.
          </p>

          <Heading id="data-deletion">7. Your Choices and Data Deletion</Heading>
          <p>You have the following options regarding your information:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Access and correction:</strong> You may request a copy of the personal
              information we hold about you, or ask us to correct information that is inaccurate.
            </li>
            <li>
              <strong>Deletion:</strong> You may ask us to delete your account and the personal
              information associated with it.
            </li>
            <li>
              <strong>Withdraw permissions:</strong> You may disconnect the App from your Facebook
              account at any time through your Facebook app settings.
            </li>
          </ul>
          <p>
            <strong>How to request deletion:</strong> Send a request to{" "}
            <a className="text-brand-mid underline" href="mailto:matt@virginiabariatric.com">
              matt@virginiabariatric.com
            </a>{" "}
            from the email address associated with your account, or remove the App under Settings
            &amp; Privacy &gt; Settings &gt; Apps and Websites on Facebook.com. We will confirm your
            identity, process the request, and confirm completion within 30 days.
          </p>

          <Heading id="security">8. Security</Heading>
          <p>
            We use commercially reasonable administrative, technical, and physical safeguards
            designed to protect your information against unauthorized access, disclosure,
            alteration, and destruction. These include encryption of data in transit, access
            controls limiting who on our team can view user data, and regular review of our security
            practices. No method of transmission or storage is completely secure, however, and we
            cannot guarantee absolute security.
          </p>

          <Heading id="childrens-privacy">9. Children&rsquo;s Privacy</Heading>
          <p>
            The App is not directed to children under 13, and we do not knowingly collect personal
            information from anyone under 13. Facebook itself requires users to be at least 13 years
            old. If we learn that we have collected information from a child under 13, we will
            delete it promptly. If you believe a child has provided us with personal information,
            please contact us at{" "}
            <a className="text-brand-mid underline" href="mailto:matt@virginiabariatric.com">
              matt@virginiabariatric.com
            </a>
            .
          </p>

          <Heading id="third-party">10. Third-Party Links and Services</Heading>
          <p>
            The App may contain links to websites or services that we do not operate. This Privacy
            Policy does not apply to those third parties, and we are not responsible for their
            content or privacy practices. We encourage you to read the privacy policy of any
            third-party service before providing it with your information.
          </p>

          <Heading id="international">11. Users Outside the United States</Heading>
          <p>
            The App is operated from the United States, and any information we collect is processed
            and stored there. If you access the App from outside the United States, you understand
            that your information will be transferred to and processed in the United States, where
            data protection laws may differ from those in your country.
          </p>

          <Heading id="changes">12. Changes to This Privacy Policy</Heading>
          <p>
            We may update this Privacy Policy from time to time. When we do, we will revise the
            &ldquo;Last Updated&rdquo; date at the top of this page. If the changes are material, we
            will provide additional notice, such as an in-app message or an email to the address
            associated with your account. Your continued use of the App after an update takes effect
            means you accept the revised policy.
          </p>

          <Heading id="contact">13. Contact Us</Heading>
          <p>
            If you have questions, concerns, or requests regarding this Privacy Policy or your
            personal information, contact us at:
          </p>
          <address className="not-italic">
            <strong>Virginia Bariatric Surgery, PC</strong>
            <br />
            171 Elden St, Ste 3B
            <br />
            Herndon, VA 20170
            <br />
            Email:{" "}
            <a className="text-brand-mid underline" href="mailto:matt@virginiabariatric.com">
              matt@virginiabariatric.com
            </a>
            <br />
            Website:{" "}
            <a className="text-brand-mid underline" href="https://virginiabariatric.com">
              virginiabariatric.com
            </a>
          </address>
        </div>
      </PageSection>
    </article>
  );
}
